import { useContext } from "react";

import { Redirect, useHistory, useParams } from "react-router-dom";

import CustomerPage from "@/components/CustomerPage";
import { hasReportEntitlement } from "@/components/reports/reportListClick";
import SliderTabs, { SliderTab } from "@/components/SliderTabs";
import { ERROR_404_URL, getWardUrl } from "@/core/urls";
import useDocTitle from "@/hooks/use-doc-title";
import { ViewerContext } from "@/tpo/Viewer";
import B2CHeader from "@/tpo/header/B2CHeader";
import { gql, useQuery } from "@apollo/client";

import { REPORTS_LIST_QUERY, WARD_ENTITLEMENTS_QUERY } from "../reports/graphql/queries";

export function WardTabs({ selectedTab, user }) {
  const { userId } = useContext(ViewerContext);
  const history = useHistory();

  const { data: reportsData } = useQuery(REPORTS_LIST_QUERY, {
    variables: { userId },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !userId
  });

  const { data: entitlementsData } = useQuery(WARD_ENTITLEMENTS_QUERY, {
    variables: { userId },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !userId
  });

  const showReportsTab =
    !!reportsData &&
    !!entitlementsData &&
    hasReportEntitlement({
      reports: reportsData.reports,
      userEntitlements: entitlementsData?.user?.entitlements
    });

  const tabs = [
    {
      label: "profile",
      urlTemplate: "/patients/:patientId/profile"
    },
    {
      label: "orders",
      urlTemplate: "/patients/:patientId/orders"
    },
    {
      label: "test results",
      urlTemplate: "/patients/:patientId/tests"
    },
    ...(showReportsTab
      ? [
          {
            label: "reports",
            urlTemplate: "/patients/:patientId/reports"
          }
        ]
      : []),
    {
      label: "questionnaire",
      urlTemplate: "/patients/:patientId/questionnaire"
    },
    {
      label: "suggestions",
      urlTemplate: "/suggestions",
      disabled: true
    },
    {
      label: "food list",
      urlTemplate: "/food-list",
      disabled: true
    }
  ];

  return (
    <SliderTabs
      data-component-name="WardTabs"
      tabs={tabs}
      selectedTab={selectedTab}
      withBorder
      renderTab={({ tab, ...props }) => (
        <SliderTab
          {...props}
          tab={tab}
          onClick={() => {
            history.push(
              getWardUrl(tab.urlTemplate, {
                patientId: userId
              })
            );
          }}
        />
      )}
    />
  );
}

const PATIENT_DETAILS = gql`
  query PatientDetails($userId: ID!) {
    user(userId: $userId) {
      id
      firstName
      lastName
    }
  }
`;

export default function Template({ children, selectedTab, bodyProps, docTitle, jumbotronProps }) {
  useDocTitle(docTitle, docTitle === undefined, "Regenerus");

  const { patientId } = useParams();

  const { data } = useQuery(PATIENT_DETAILS, {
    variables: {
      userId: patientId
    }
  });

  const user = data?.user;

  return (
    <>
      <B2CHeader hideNav />
      <CustomerPage
        bg="haze"
        jumbotronProps={{
          bg: "purple",
          color: "white",
          title: user ? `${user.firstName} ${user.lastName}` : null,
          ...jumbotronProps
        }}
        bodyProps={bodyProps}
      >
        {data ? (
          user ? (
            <>
              <WardTabs selectedTab={selectedTab} user={user} />
              {children}
            </>
          ) : (
            <Redirect to={ERROR_404_URL} />
          )
        ) : null}
      </CustomerPage>
    </>
  );
}
